import type { CarrierColumn } from "@platform/api";

/**
 * Orders carrier columns by the analyst's shared sequence over an alphabetical
 * base — the default order every Present tab and the exported MPX spreadsheet
 * share. The payload's own order is plan-group arrival order, which no tab
 * renders, so it is sorted away before the sequence is applied: a carrier the
 * sequence doesn't name (added after the analyst arranged one, or missing from
 * a partial order) lands alphabetically rather than wherever the payload
 * happened to put it, which is what the Responses tab and the Quotes grid
 * already do (CD-2573).
 */
export const applyCarrierColumnOrder = (
  carriers: readonly CarrierColumn[],
  carrierOrder: readonly string[] | undefined,
): CarrierColumn[] => {
  const alphabetical = carriers.toSorted((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (!carrierOrder || carrierOrder.length === 0) {
    return alphabetical.map((carrier, index) => ({ ...carrier, order: index }));
  }

  const rank = new Map<string, number>();
  carrierOrder.forEach((id, index) => rank.set(id, index));

  return alphabetical
    .map((carrier, index) => ({ carrier, index }))
    .sort((a, b) => {
      const rankA = rank.get(a.carrier.id) ?? Infinity;
      const rankB = rank.get(b.carrier.id) ?? Infinity;

      return rankA === rankB ? a.index - b.index : rankA - rankB;
    })
    .map(({ carrier }, index) => ({ ...carrier, order: index }));
};
